import { useEffect, useState } from 'react';
import { useListStore } from '@/stores/list';
import { useKioskStore } from '@/stores/kiosk';
import { useUsersStore } from '@/stores/users';
import { getAutoTimezoneId, getTimezoneList } from '@/utils/convertDate';

type TimezoneVariant = 'kiosk' | 'users';

/**
 * Hook for fetching timezones and automatically updating timezone when autotimezone is enabled.
 * If timezones are already loaded, the hook does not make an additional request.
 * Automatically updates timezone for kiosk or user depending on the variant.
 * @param variant - The variant of the timezones to process ('kiosk' or 'users').
 * @returns A boolean flag indicating whether timezones are still loading or being updated.
 */
export const useAutoTimezone = (variant: TimezoneVariant = 'users') => {
  const [isUpdatingTimezone, setIsUpdatingTimezone] = useState(false);

  const timezones = useListStore((state) => state.timezones);

  const { kioskData, updateKioskTimezone } = useKioskStore((state) => ({
    kioskData: state.kioskData,
    updateKioskTimezone: state.updateKioskTimezone,
  }));

  const { userData, saveUserInfo } = useUsersStore((state) => ({
    userData: state.userData,
    saveUserInfo: state.saveUserInfo,
  }));

  useEffect(() => {
    if (!timezones.length) return;

    const timezoneList = getTimezoneList(timezones);
    const autoTimezone = getAutoTimezoneId(timezoneList);

    if (!autoTimezone) {
      return;
    }

    const isKioskVariant = variant === 'kiosk';

    // KIOSK
    if (
      isKioskVariant &&
      kioskData?.isAutotimezone &&
      autoTimezone !== kioskData.timezone
    ) {
      const updateCurrentKioskTimezone = async () => {
        setIsUpdatingTimezone(true);
        try {
          await updateKioskTimezone(autoTimezone);
        } catch (err) {
          console.error('Failed to update kiosk timezone:', err);
        } finally {
          setIsUpdatingTimezone(false);
        }
      };

      void updateCurrentKioskTimezone();
      return;
    }

    // USER
    if (
      !isKioskVariant &&
      userData?.isAutotimezone &&
      autoTimezone !== userData.timezone
    ) {
      const updateUserTimezone = async () => {
        setIsUpdatingTimezone(true);
        try {
          // Weekly availability is wall time, so it keeps the same hours in the new timezone.
          await saveUserInfo({
            isAutotimezone: true,
            timezone: autoTimezone,
          });
        } catch (err) {
          console.error('Failed to update user timezone:', err);
        } finally {
          setIsUpdatingTimezone(false);
        }
      };

      void updateUserTimezone();
    }
  }, [
    variant,
    timezones,
    kioskData?.isAutotimezone,
    kioskData?.timezone,
    userData?.isAutotimezone,
    userData?.timezone,
    updateKioskTimezone,
    saveUserInfo,
  ]);

  return isUpdatingTimezone;
};
